/**
 * Group footers (spec M1-B4 §5.5, §8.10, `[B5§5.3]`).
 *
 * On the server every one of these is a SQL aggregate over the shadow columns; here they run
 * over the loaded page, which is what makes the fixture source honest about the shape without
 * pretending to be the database. The split is deliberate: everything computable from the
 * `text` / `number` / `date` shadows is generic and lives here, and only the aggregations that
 * need the type's own vocabulary (the status battery, the people avatars) are delegated to
 * `ColumnTypeUi.summarise` — so no `switch (columnType)` exists outside a type folder.
 */
import {
  defaultAggregationOf,
  findColumnType,
  formatSummaryDate,
  formatSummaryNumber,
  formatSummaryPair,
  type ColumnAggregation,
} from '@elizmonday/shared';
import { getColumnUi } from '../columns/registry';
import { valueContext } from '../columns/context';
import type { CellRenderContext } from '../columns/types';
import type { BoardColumn, SummaryValue } from '../data/types';

/** `hide_footer: true` suppresses a column's footer cell `[B5§5.3 F7]`. */
export function footerHidden(column: BoardColumn): boolean {
  const settings = column.settings as { hide_footer?: boolean } | null;
  return settings?.hide_footer === true;
}

/** The aggregation a column's footer renders: the chosen one, else the type's default. */
export function defaultAggregation(column: BoardColumn): ColumnAggregation {
  const shared = findColumnType(column.type);
  return column.aggregation ?? (shared ? defaultAggregationOf(shared) : 'none');
}

/** P-027: the one formatter, shared with the api path (`data/trpc-source.ts`). */
const formatNumber = formatSummaryNumber;

function median(values: readonly number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const middle = sorted.length >> 1;
  if (sorted.length % 2 === 1) return sorted[middle]!;
  return (sorted[middle - 1]! + sorted[middle]!) / 2;
}

/** monday's short board date, never an ISO timestamp (P-027). */
function formatDate(date: Date): string {
  return formatSummaryDate(date) ?? '';
}

/**
 * One footer value for one column over the cells of one group.
 * `cells` holds the **canonical** values, `null` for an empty cell — exactly what the group
 * page carries, so the caller never has to know how a type stores anything.
 */
export function summarise(
  column: BoardColumn,
  cells: readonly unknown[],
  context: CellRenderContext,
  aggregation: ColumnAggregation = defaultAggregation(column),
): SummaryValue | null {
  if (aggregation === 'none' || footerHidden(column)) return null;

  const ui = getColumnUi(column.type);
  const own = ui.summarise?.(aggregation, cells, column.settings, context);
  if (own) return own;

  const shared = findColumnType(column.type);
  const filled = cells.filter((cell) => cell !== null && cell !== undefined);

  switch (aggregation) {
    case 'count':
      return { aggregation, text: String(cells.length) };
    case 'count_filled':
      return { aggregation, text: String(filled.length) };
    case 'count_empty':
      return { aggregation, text: String(cells.length - filled.length) };
    default:
      break;
  }

  if (!shared) return null;
  const ctx = valueContext(context);
  const keys = cells.map((cell) => shared.toSortKeys(cell, column.settings, ctx));
  const numbers = keys.map((key) => key.number).filter((value): value is number => value !== null);
  const dates = keys.map((key) => key.date).filter((value): value is Date => value !== null);

  switch (aggregation) {
    case 'sum':
      return { aggregation, text: formatNumber(numbers.reduce((total, value) => total + value, 0)) };
    case 'avg':
      return numbers.length === 0
        ? { aggregation, text: '' }
        : {
            aggregation,
            text: formatNumber(numbers.reduce((total, value) => total + value, 0) / numbers.length),
          };
    case 'median':
      return numbers.length === 0
        ? { aggregation, text: '' }
        : { aggregation, text: formatNumber(median(numbers)) };
    case 'min':
      return numbers.length === 0
        ? { aggregation, text: '' }
        : { aggregation, text: formatNumber(Math.min(...numbers)) };
    case 'max':
      return numbers.length === 0
        ? { aggregation, text: '' }
        : { aggregation, text: formatNumber(Math.max(...numbers)) };
    case 'earliest':
      return dates.length === 0
        ? { aggregation, text: '' }
        : { aggregation, text: formatDate(new Date(Math.min(...dates.map((date) => date.getTime())))) };
    case 'latest':
      return dates.length === 0
        ? { aggregation, text: '' }
        : { aggregation, text: formatDate(new Date(Math.max(...dates.map((date) => date.getTime())))) };
    case 'range': {
      if (dates.length === 0) return { aggregation, text: '' };
      const times = dates.map((date) => date.getTime());
      const from = new Date(Math.min(...times));
      const to = new Date(Math.max(...times));
      return { aggregation, text: formatSummaryPair(aggregation, from, to), detail: { from, to } };
    }
    default:
      // `battery` and `avatars` reached this point only because the type declined them.
      return null;
  }
}

/** The label the footer menu shows for an aggregation. monday's own footer vocabulary. */
export const AGGREGATION_LABELS: Readonly<Record<string, string>> = {
  none: 'None',
  sum: 'Sum',
  avg: 'Average',
  median: 'Median',
  min: 'Min',
  max: 'Max',
  count: 'Count',
  count_filled: 'Filled',
  count_empty: 'Empty',
  earliest: 'Earliest',
  latest: 'Latest',
  range: 'Range',
  battery: 'Battery',
  avatars: 'Avatars',
};
